async function loadFunctions() {
  const { getFunctions, httpsCallable } = await import('firebase/functions')
  const functions = getFunctions(undefined, 'australia-southeast1')
  return { functions, httpsCallable }
}

export async function redirectToCheckout(tier: 'storefront' | 'dealership'): Promise<void> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const createCheckout = httpsCallable<{ tier: string }, { url: string }>(
      functions,
      'stripeCreateCheckout',
    )

    const result = await createCheckout({ tier })
    window.location.href = result.data.url
  } catch (error) {
    console.error('Checkout failed:', error)
    throw new Error('Failed to start checkout. Please try again.')
  }
}

/**
 * Call the Cloud Function to create a Stripe Customer Portal session,
 * then redirect the user to manage their subscription.
 */
/**
 * Call the Cloud Function to create a Stripe Checkout session for a product order.
 * Each call handles one seller's items. Returns the checkout URL and order ID.
 */
export async function createOrderCheckout(data: {
  items: Array<{ listingId: string; quantity: number }>
  shippingOption: 'pickup' | 'courier' | 'post'
  shippingAddress?: {
    name: string
    line1: string
    line2?: string
    city: string
    region: string
    postcode: string
  }
}): Promise<{ url: string; orderId: string }> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const callFn = httpsCallable<typeof data, { url: string; orderId: string }>(
      functions,
      'stripeCreateOrderCheckout',
    )
    const result = await callFn(data)
    return result.data
  } catch (error) {
    console.error('Order checkout failed:', error)
    throw new Error('Failed to start order checkout. Please try again.')
  }
}

export async function createDirectOrder(data: {
  items: Array<{ listingId: string; quantity: number }>
  shippingOption: 'pickup' | 'courier' | 'post'
  shippingAddress?: {
    name: string
    line1: string
    line2?: string
    city: string
    region: string
    postcode: string
  }
  paymentMethod: 'bank_transfer' | 'cash'
}): Promise<{ orderId: string }> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const callFn = httpsCallable<typeof data, { orderId: string }>(
      functions,
      'orderCreateDirect',
    )
    const result = await callFn(data)
    return result.data
  } catch (error) {
    console.error('Direct order failed:', error)
    throw new Error('Failed to create order. Please try again.')
  }
}

export async function createFundraiserCheckout(data: {
  listingId: string
  items: Array<{
    type: 'product' | 'raffle' | 'sponsorship'
    itemId: string
    quantity: number
    customAmount?: number
  }>
  reference?: string
}): Promise<{ url: string; orderId: string }> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const callFn = httpsCallable<typeof data, { url: string; orderId: string }>(
      functions,
      'stripeCreateFundraiserCheckout',
    )
    const result = await callFn(data)
    return result.data
  } catch (error: any) {
    console.error('Fundraiser checkout failed:', error)
    const serverMessage = error?.code === 'functions/failed-precondition'
      ? error.message
      : null
    throw new Error(serverMessage || 'Failed to start checkout. Please try again.')
  }
}

export type PortalFlow =
  | { type: 'payment_method_update' }
  | { type: 'subscription_cancel' }
  | { type: 'subscription_update' }

export async function redirectToPortal(flow?: PortalFlow): Promise<void> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const createPortal = httpsCallable<{ flow?: PortalFlow }, { url: string }>(
      functions,
      'stripeCreatePortal',
    )

    const result = await createPortal(flow ? { flow } : {})
    window.location.href = result.data.url
  } catch (error) {
    console.error('Customer portal failed:', error)
    throw new Error('Failed to open subscription management. Please try again.')
  }
}

export async function redirectToConnectOnboarding(): Promise<void> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const createConnect = httpsCallable<Record<string, never>, { url: string }>(
      functions,
      'stripeCreateConnectAccount',
    )
    const result = await createConnect({})
    window.location.href = result.data.url
  } catch (error: any) {
    console.error('Stripe Connect onboarding failed:', error)
    const serverMessage =
      error?.code === 'functions/failed-precondition' ? error.message : null
    throw new Error(serverMessage || 'Failed to start seller onboarding. Please try again.')
  }
}

export async function syncConnectStatus(): Promise<{ status: 'pending' | 'active' | 'restricted' }> {
  const { functions, httpsCallable } = await loadFunctions()
  const callFn = httpsCallable<Record<string, never>, { status: 'pending' | 'active' | 'restricted' }>(
    functions,
    'stripeSyncConnectStatus',
  )
  const result = await callFn({})
  return result.data
}

export async function redirectToConnectDashboard(): Promise<void> {
  try {
    const { functions, httpsCallable } = await loadFunctions()
    const getDashboard = httpsCallable<Record<string, never>, { url: string }>(
      functions,
      'stripeGetConnectDashboard',
    )
    const result = await getDashboard({})
    window.location.href = result.data.url
  } catch (error) {
    console.error('Stripe Connect dashboard failed:', error)
    throw new Error('Failed to open payout dashboard. Please try again.')
  }
}

export async function createOrderPaymentSession(orderId: string): Promise<{ url: string }> {
  const { functions, httpsCallable } = await loadFunctions()
  const callFn = httpsCallable<{ orderId: string }, { url: string }>(
    functions,
    'stripeCreateOrderPaymentSession',
  )
  const result = await callFn({ orderId })
  return result.data
}

export async function fetchOrderReceiptUrl(orderId: string): Promise<string> {
  const { functions, httpsCallable } = await loadFunctions()
  const fn = httpsCallable(functions, 'stripeGetReceiptUrl')
  const result = await fn({ orderId })
  return (result.data as { receiptUrl: string }).receiptUrl
}

export interface SubscriptionInvoice {
  id: string
  number: string | null
  created: number
  status: string
  periodEnd: number | null
  amount: number
  currency: string
  hostedInvoiceUrl: string | null
  invoicePdf: string | null
}

export async function fetchSubscriptionInvoices(): Promise<SubscriptionInvoice[]> {
  const { functions, httpsCallable } = await loadFunctions()
  const fn = httpsCallable(functions, 'stripeListSubscriptionInvoices')
  const result = await fn({})
  return (result.data as { invoices: SubscriptionInvoice[] }).invoices
}
